import React, { useRef, useState, useEffect } from 'react'

type Props = {
	length?: number
	autoFocus?: boolean
	onComplete?: (code: string) => void
	placeholder?: string
	showResend?: boolean
	resendSeconds?: number
}

export const OtpInput = ({
	length = 4,
	autoFocus = true,
	onComplete,
	placeholder = ''
}: Props) => {
	const [values, setValues] = useState<string[]>(() => Array(length).fill(''))
	const inputsRef = useRef<Array<HTMLInputElement | null>>([])

	useEffect(() => {
		if (autoFocus) inputsRef.current[0]?.focus()
	}, [autoFocus])

	useEffect(() => {
		onComplete?.(values.join(''))
	}, [values, onComplete])

	const focusInput = (index: number) => {
		const el = inputsRef.current[index]
		if (el) el.focus()
	}

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement>,
		idx: number
	) => {
		const raw = e.target.value || ''
		const lastChar = raw.replace(/[^0-9]/g, '').slice(-1)
		if (!lastChar && raw !== '') return

		setValues(prev => {
			const next = [...prev]
			next[idx] = lastChar || ''
			return next
		})

		if (lastChar) {
			if (idx < length - 1) focusInput(idx + 1)
		}
	}

	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLInputElement>,
		idx: number
	) => {
		const key = e.key
		if (key === 'Backspace') {
			if (values[idx]) {
				setValues(prev => {
					const next = [...prev]
					next[idx] = ''
					return next
				})
			} else if (idx > 0) {
				focusInput(idx - 1)
				setValues(prev => {
					const next = [...prev]
					next[idx - 1] = ''
					return next
				})
			}
		} else if (key === 'ArrowLeft' && idx > 0) {
			focusInput(idx - 1)
		} else if (key === 'ArrowRight' && idx < length - 1) {
			focusInput(idx + 1)
		}
	}

	const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
		e.preventDefault()
		const pasted = e.clipboardData.getData('Text').replace(/\D/g, '')
		if (!pasted) return
		const toFill = pasted.slice(0, length).split('')
		setValues(prev => {
			const next = [...prev]
			for (let i = 0; i < length; i++) {
				next[i] = toFill[i] ?? ''
			}
			return next
		})
		const last = Math.min(toFill.length, length) - 1
		if (last >= 0) focusInput(last + 1 >= length ? length - 1 : last + 1)
	}

	return (
		<div className="flex justify-center gap-2 min-[375px]:gap-3 md:gap-4">
			{Array.from({ length }).map((_, idx) => (
				<input
					key={idx}
					aria-label={`OTP digit ${idx + 1}`}
					inputMode="numeric"
					pattern="[0-9]*"
					maxLength={1}
					placeholder={placeholder}
					value={values[idx]}
					onChange={e => handleChange(e, idx)}
					onKeyDown={e => handleKeyDown(e, idx)}
					onPaste={handlePaste}
					ref={el => (inputsRef.current[idx] = el)}
					className="h-12 w-12 min-[375px]:h-14 min-[375px]:w-14 md:h-16 md:w-16 rounded-full border border-[#366f9a] bg-white text-center text-xl min-[375px]:text-2xl font-semibold text-[#244f70] outline-none transition focus:border-[#1f5f8f] focus:ring-2 focus:ring-[#7ba7ca]/50"
				/>
			))}
		</div>
	)
}
